// @flow
import type { UserInfo } from "@tvg/types/User";
import type {
  SetNewPreferenceValue,
  UpdateBalanceActionType,
  UpdateWalletBalancesActionType,
  FailBalanceRequestAction,
  SessionStartUpdate
} from "@tvg/shared-actions/UserActions";
import type {
  SetIsVerified,
  SetUserJurisdiction
} from "@fdr/shared-actions/UserActions";
import type { UserOptedInPromos } from "../types";

export type UserData = {
  error: boolean | null,
  hasRequested: boolean,
  logged: boolean,
  logging: boolean,
  user: UserInfo,
  userLogout: boolean,
  loginPin: number,
  accountNumber: string,
  sessionStartAt?: Date
};

export type PreferencesBetAmount = {
  WN: string,
  PL: string,
  SH: string,
  EX: string,
  TR: string,
  SU: string,
  SH5: string,
  P: string
};

export type UserBetPrefs = {
  defaultBetType: string,
  useCustomBetAmount: boolean,
  useMinimumBetAmount: boolean,
  default_bet_amount: PreferencesBetAmount
};

export type UserSessionSuccessAction = {
  type: "USER_DATA_UPDATE",
  payload: UserData
};

export type UserPrefsSet = {
  type: "USER_PREFS_SET",
  payload: {
    preferences: mixed,
    gotPreferences: boolean
  }
};

export type UserPrefsFail = {
  type: "USER_PREFS_FAIL",
  payload: {},
  error: true
};

export type UserPromo = {
  id: number,
  optedIn: boolean,
  path: string
};

export type UserPromosSuccess = {
  type: "USER_PROMOS_SUCCESS",
  payload: { optedInPromos: UserOptedInPromos }
};

export type UserPromosFail = {
  type: "USER_PROMOS_FAIL",
  payload: {},
  error: true
};

export type UpdateBalancePollerAction = {
  type: "USER_BALANCE_POLLER",
  payload: number
};

export type SetUserLoggedState = {
  type: "USER_LOGGED_STATE",
  payload: {
    isLogged: boolean
  }
};

export type SetUserAccountNumber = {
  type: "USER_ACCOUNT_NUMBER",
  payload: {
    accountNumber: string
  }
};

export type UserPrefsBalanceShownAction = {
  type: "USER_PREFS_BALANCE_SHOWN",
  payload: {
    balance_is_shown: boolean
  }
};

export type UserDefaultBetPrefsAction = {
  type: "SET_DEFAULT_BET_PREFERENCES",
  payload: UserBetPrefs
};

export type UserPrefsBetConfirmationAction = {
  type: "USER_PREFS_BET_CONFIRMATION",
  payload: {
    balance_is_shown: boolean
  }
};

export type UserLogout = {
  type: "USER_LOGOUT"
};

export type UserActions =
  | UserSessionSuccessAction
  | UpdateBalanceActionType
  | UpdateWalletBalancesActionType
  | UpdateBalancePollerAction
  | FailBalanceRequestAction
  | SetUserLoggedState
  | UserDefaultBetPrefsAction
  | SetUserAccountNumber
  | UserPrefsSet
  | UserPrefsFail
  | UserPrefsBalanceShownAction
  | UserPrefsBetConfirmationAction
  | UserPromosSuccess
  | UserPromosFail
  | SetNewPreferenceValue
  | SetIsVerified
  | SetUserJurisdiction
  | UserLogout
  | SessionStartUpdate;

export const getUserData = (userData: UserData): UserSessionSuccessAction => ({
  type: "USER_DATA_UPDATE",
  payload: userData
});

export const successBalancePollerRequest = (
  balancePoller: number
): UpdateBalancePollerAction => ({
  type: "USER_BALANCE_POLLER",
  payload: balancePoller
});

export const setUserPrefs = (
  userPrefs: mixed = {},
  gotPreferences: boolean = false
): UserPrefsSet => ({
  type: "USER_PREFS_SET",
  payload: {
    preferences: userPrefs,
    gotPreferences
  }
});

export const failUserPrefs = (err: Error): UserPrefsFail => ({
  type: "USER_PREFS_FAIL",
  payload: err,
  error: true
});

export const successUserPromos = (
  userPromos: UserOptedInPromos
): UserPromosSuccess => ({
  type: "USER_PROMOS_SUCCESS",
  payload: { optedInPromos: userPromos }
});

export const failUserPromos = (err: Error): UserPromosFail => ({
  type: "USER_PROMOS_FAIL",
  payload: err,
  error: true
});

export const setUserLoggedState = (isLogged: boolean): SetUserLoggedState => ({
  type: "USER_LOGGED_STATE",
  payload: {
    isLogged
  }
});

export const setUserAccountNumber = (
  accountNumber: string
): SetUserAccountNumber => ({
  type: "USER_ACCOUNT_NUMBER",
  payload: {
    accountNumber
  }
});
